import { Container, SceneObject } from '@cgs/wge';
import { ReelsEngine, ReelsEngineSystemUpdateOrders } from '../../../reels_engine/reels_engine';
import {
  T_ISlotGameEngineProvider,
  T_IconsSceneObjectComponent,
  T_SceneCache,
} from '../../../type_definitions';
import { IconsSceneObjectComponent } from '../icons_scene_object_component';
import { ResponseDependentGameComponentProvider } from '../response_dependent_game_component_provider';
import { SceneCache } from '../scene_cache';
import { IconValueDescription } from './icon_value_description';
import { IconWithValuesPlacementSystem } from './systems/icon_with_values_placement_system';
import { IconWithValuesPortalSystem } from './systems/icon_with_values_portal_system';
import { IconWithValuesRenderSystem } from './systems/icon_with_values_render_system';
import { IReelsEngineProvider } from '../../../reels_engine/game_components_providers/i_reels_engine_provider';

export const ICON_WITH_VALUES_PROVIDER_TOKEN = Symbol('ICON_WITH_VALUES_PROVIDER_TOKEN');

export class AbstractIconWithValuesProvider extends ResponseDependentGameComponentProvider {
  protected readonly _reelsEngine: ReelsEngine;
  protected readonly _iconNodeProvider: IconsSceneObjectComponent;
  protected readonly _sceneCache: SceneCache;
  protected _renderSystem: IconWithValuesRenderSystem;
  protected readonly _valueDescriptions: Map<number, IconValueDescription> = new Map();
  private readonly _usedValueScenes: SceneObject[] = [];

  constructor(container: Container) {
    super(container);

    this._reelsEngine =
      container.resolve<IReelsEngineProvider>(T_ISlotGameEngineProvider).reelsEngine;
    this._iconNodeProvider = container.resolve<IconsSceneObjectComponent>(
      T_IconsSceneObjectComponent
    );
    this._sceneCache = container.resolve<SceneCache>(T_SceneCache);

    this.gameStateMachine.waitRequest.leaved.listen(() => this.createValues());
    this.gameStateMachine.stop.leaved.listen(() => this.clearValues());
    this.createSystems();
  }

  public deinitialize(): void {
    super.deinitialize();
    for (const scene of this._usedValueScenes) {
      scene.deinitialize();
    }
  }

  public registerValueScene(scene: SceneObject): void {
    if (!this._usedValueScenes.includes(scene)) {
      this._usedValueScenes.push(scene);
    }
  }

  public unregisterValueScene(scene: SceneObject): void {
    const index = this._usedValueScenes.indexOf(scene);
    if (index !== -1) {
      this._usedValueScenes.splice(index, 1);
    }
  }

  public getValueDescription(reel: number, line: number): IconValueDescription | null {
    const position = this._reelsEngine.iconAnimationHelper.getPosition(reel, line);
    if (this._valueDescriptions.has(position)) {
      return this._valueDescriptions.get(position) as IconValueDescription;
    }
    return null;
  }

  protected createSystems(): void {
    const portalSystem = new IconWithValuesPortalSystem(
      this._reelsEngine.entityEngine,
      this._reelsEngine.entityCacheHolder,
      this._reelsEngine.internalConfig
    ).withInitialize();
    portalSystem.updateOrder = ReelsEngineSystemUpdateOrders.portalSystemUpdateOrder - 1;
    portalSystem.register();

    const placementSystem = new IconWithValuesPlacementSystem(
      this._reelsEngine.entityEngine,
      this._reelsEngine.entityCacheHolder,
      this
    ).withInitialize();
    placementSystem.updateOrder =
      ReelsEngineSystemUpdateOrders.symbolPlacementSystemUpdateOrder - 1;
    placementSystem.register();

    this._renderSystem = new IconWithValuesRenderSystem(
      this.container,
      this._reelsEngine.entityEngine,
      this._reelsEngine.entityCacheHolder,
      this._iconNodeProvider.iconsRender,
      this._iconNodeProvider.animIconsRender,
      this._sceneCache,
      this
    ).withInitialize() as IconWithValuesRenderSystem;
    this._renderSystem.updateOrder = ReelsEngineSystemUpdateOrders.renderSystemUpdateOrder + 1;
    this._renderSystem.register();
  }

  protected createValues(): void {
    this._valueDescriptions.set(0, new IconValueDescription('0', '0', 0, 0, '0'));
  }

  protected clearValues(): void {
    this._valueDescriptions.clear();
  }
}
